import { apiClient } from './client';

/**
 * Content-Disposition 에서 저장 이름을 고른다(RFC 6266) — 한국어 이름을 싣는 `filename*=UTF-8''…` 가
 * 있으면 그것을, 없으면 `filename="…"`, 둘 다 없으면 null.
 *
 * 2026-10-09 — 기준일 마감본 CSV 를 한국어 이름(`로봇목록_2026-09-30_기준.csv`)으로 받게 하면서 `filename*`
 * 도 읽는다. 서명 문서 PDF 는 서버가 `filename*` 만 싣는데, 호출부가 넘기는 fallbackName 이 서버와 같은
 * 규칙의 같은 이름이라 저장 이름은 그대로다.
 */
export function filenameFromDisposition(disposition: string): string | null {
  const star = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(disposition);
  if (star?.[1]) {
    try {
      const decoded = decodeURIComponent(star[1].trim());
      if (decoded) return decoded;
    } catch {
      // 깨진 퍼센트 인코딩 — 아래 filename="…" 로 넘어간다.
    }
  }
  const plain = /filename\s*=\s*"([^"]+)"/i.exec(disposition);
  return plain?.[1] ?? null;
}

/**
 * Fetch an authenticated blob from the API and trigger a browser download.
 * Takes the file name from `Content-Disposition` when present (see
 * filenameFromDisposition); falls back to the supplied `fallbackName`.
 *
 * Used for CSV / Excel exports where `window.open()` wouldn't carry the
 * Authorization header.
 */
export async function downloadBlob(
  path: string,
  // Accept any plain object — the typed query DTOs (ListReportsQuery,
  // ListRobotsQuery, etc.) all satisfy the structural shape but aren't
  // assignable to `Record<string, unknown>` due to optional-property
  // exact-match semantics under strict mode.
  params: object | undefined,
  fallbackName: string,
): Promise<void> {
  const res = await apiClient.get<Blob>(path, {
    params,
    responseType: 'blob',
  });

  const disposition = res.headers['content-disposition'] ?? '';
  const filename = filenameFromDisposition(String(disposition)) ?? fallbackName;

  const blob = res.data;
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Revoke asynchronously to let the click fire first (some browsers
    // cancel the download if the URL is revoked synchronously).
    setTimeout(() => URL.revokeObjectURL(url), 1_000);
  }
}
